/**
 * Text rendering of descriptor-typed grid values for export.
 *
 * A grid cell renders a value as a chip, a link, or a formatted date; a CSV
 * cell has only text. This module is the text half of that pair, reading the
 * same descriptor type the cell renderers read so a status the screen shows as
 * "Active" is not a bare `false` in the file the user opens.
 *
 * Contains:
 * - descriptorExportValue — descriptor value + column type → export string
 *
 * Returns plain JS — no JSX.
 */

import { formatGridDateTime } from '@sophocles/data-grids/grid/valueFormats';

/**
 * Render one descriptor-typed value as export text.
 *
 * @param {*} value - The raw row value for this column.
 * @param {object} column - A normalized column from `normalizeDescriptorColumn`.
 * @param {object} [opts]
 * @param {object} [opts.formatters] - config.formatters — boolean and datetime
 *   formatters, so the file states values the way the grid does.
 * @param {Function} [opts.translate] - i18next translator, for enum option labels.
 * @param {Object<string, string>} [opts.terms] - Tenant vocabulary, keyed by
 *   term field name, interpolated into enum option labels.
 * @param {Function} [opts.resolveReference] - config.resolveReference — resolves
 *   a `{ type, id, name }` reference to `{ label, route }`; only the label is used.
 * @returns {string} The export text; `''` for an empty value.
 */
export function descriptorExportValue(value, column, { formatters, translate, terms, resolveReference } = {}) {
  if (value === null || value === undefined) return '';

  const { type, options, format } = column;

  switch (type) {
    case 'datetime':
      return formatGridDateTime(value, { dateOnly: format === 'date', datetimeFormatter: formatters?.datetime });

    case 'boolean':
      if (formatters?.boolean) return formatters.boolean(value);
      return translate ? translate(value ? 'general.yes' : 'general.no') : String(value);

    case 'enum': {
      const option = (options ?? []).find(opt => opt.value === value);
      if (!option) return String(value);
      return translate ? translate(option.label_key, option.label_key, terms) : option.label_key;
    }

    case 'reference':
      return (resolveReference ? resolveReference(value)?.label : value?.name) ?? '';

    default:
      // A multi-value cell (a site's relay loads, its active alarms) reads as a
      // list, not as String()'s bare comma-join.
      if (Array.isArray(value)) return value.join(', ');
      // A reference-shaped value under an untyped column: the descriptor does
      // not label every `{type,id,name}` field as `type: "reference"`, and
      // String() on one yields "[object Object]".
      if (typeof value === 'object') return value.name ?? '';
      return String(value);
  }
}
